<template>
  <View v-if="platforms.length === 0" gap="sm" align="center">
    <Text variant="label-medium">-</Text>
  </View>

  <!--
    Collapsed by default: circular logos overlap into an avatar group (Figma "Listed on").
    Open state — desktop hover or a tap on touch — spreads them apart and reveals each
    platform's syndication status badge.
  -->
  <div v-else ref="rootRef" class="syndication-avatars" :class="{ 'is-open': isOpen }" @click="onRootClick">
    <component
      :is="item.isLink ? 'a' : 'div'"
      v-for="(item, index) in items"
      :key="item.name"
      class="platform-avatar"
      :class="{ clickable: item.isLink }"
      :style="{ zIndex: items.length - index }"
      :href="item.isLink ? item.url : undefined"
      :target="item.isLink ? '_blank' : undefined"
      :rel="item.isLink ? 'noopener noreferrer' : undefined"
      :title="item.comments"
    >
      <img :src="item.logo" :alt="item.name" class="avatar-image" />
      <span v-if="item.status.badgeColor" :class="['status-badge', item.status.badgeColor]">
        <Icon :name="item.status.iconName!" class="status-icon" />
      </span>
    </component>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import apartmentsLogo from 'icons/apartments-logo.png';
import hotpadsLogo from 'icons/hotpads-logo.png';
import realtorLogo from 'icons/realtor-logo.png';
import truliaLogo from 'icons/trulia-logo.png';
import zillowLogo from 'icons/zillow-logo.png';
import zumperLogo from 'icons/zumper-logo.png';
import { computed, ref } from 'vue';

import { Icon, Text, View } from '@tallo/design-system';

import { SyndicationPlatformDto } from '../dtos/syndication-platforms-response.dto.interface';

const props = defineProps<{
  platforms: SyndicationPlatformDto[];
}>();

// Platform status types
type PlatformStatus = 'posted' | 'deleted' | 'blocked' | 'starting' | 'unknown';

interface PlatformStatusInfo {
  status: PlatformStatus;
  isClickable: boolean;
  badgeColor: 'green' | 'red' | 'grey' | null;
  iconName: 'check' | 'x-close' | 'clock-fast-forward' | null;
}

// Map platform names to their respective logos
const platformLogos: Record<string, string> = {
  zillow: zillowLogo,
  hotpads: hotpadsLogo,
  trulia: truliaLogo,
  zumper: zumperLogo,
  'apartments-dot-com': apartmentsLogo,
  'realtor-dot-com': realtorLogo,
};

function getPlatformLogo(platformName: string): string {
  return platformLogos[platformName.toLowerCase()] || zillowLogo;
}

function getPlatformStatus(comments: string): PlatformStatusInfo {
  const normalizedComments = comments.toLowerCase().trim();

  if (normalizedComments === 'posted') {
    return {
      status: 'posted',
      isClickable: true,
      badgeColor: 'green',
      iconName: 'check',
    };
  }

  if (normalizedComments === 'starting syndication') {
    return {
      status: 'starting',
      isClickable: false,
      badgeColor: 'grey',
      iconName: 'clock-fast-forward',
    };
  }

  if (normalizedComments.includes('not posted') && normalizedComments.includes('deleted')) {
    return {
      status: 'deleted',
      isClickable: false,
      badgeColor: 'red',
      iconName: 'x-close',
    };
  }

  if (normalizedComments.includes('not posted') && normalizedComments.includes('blocked')) {
    return {
      status: 'blocked',
      isClickable: false,
      badgeColor: 'red',
      iconName: 'x-close',
    };
  }

  // Unknown status - treat as non-clickable with no badge
  return {
    status: 'unknown',
    isClickable: false,
    badgeColor: null,
    iconName: null,
  };
}

const items = computed(() =>
  props.platforms.map((platform) => {
    const status = getPlatformStatus(platform.comments);
    return {
      ...platform,
      logo: getPlatformLogo(platform.name),
      status,
      isLink: status.isClickable && !!platform.url,
    };
  }),
);

const rootRef = ref<HTMLElement>();
const isOpen = ref(false);
// Touch devices have no hover, so the group is opened with an explicit tap instead.
const canHover = typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;

function onRootClick(event: MouseEvent) {
  // On touch devices the first tap opens the group (instead of following a platform
  // link); once open, taps fall through to the links as usual.
  if (!canHover && !isOpen.value) {
    event.preventDefault();
    isOpen.value = true;
  }
}

onClickOutside(rootRef, () => {
  isOpen.value = false;
});
</script>

<style scoped lang="scss">
.syndication-avatars {
  display: inline-flex;
  align-items: center;
  // Keep the avatars' overlap z-indexes from stacking above page overlays like modals.
  isolation: isolate;
}

.platform-avatar {
  position: relative;
  box-sizing: border-box;
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  // White ring separates the circles where they overlap.
  border: 2px solid var(--t-color-neutral-white);
  background-color: var(--t-color-neutral-white);
  text-decoration: none;
  transition: margin-left 200ms ease;

  &:not(:first-child) {
    margin-left: -0.375rem; // 6px overlap, matching Figma
  }

  &.clickable {
    cursor: pointer;
  }
}

.avatar-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  // Muted by default so the colourful brand marks don't add noise to the header;
  // full colour returns when the group is hovered/opened (alongside the status badges).
  filter: grayscale(1);
  transition: filter 200ms ease;
}

// Open state (desktop hover or tapped open on touch): spread the avatars apart
// and reveal each platform's status badge.
.syndication-avatars:hover,
.syndication-avatars.is-open {
  .platform-avatar:not(:first-child) {
    margin-left: 0.125rem;
  }

  .avatar-image {
    filter: none;
  }

  .status-badge {
    opacity: 1;
    transform: scale(1);
  }
}

.status-badge {
  --status-badge-size: 0.75rem;

  position: absolute;
  bottom: -2px;
  right: -2px;

  width: var(--status-badge-size);
  height: var(--status-badge-size);

  display: flex;
  justify-content: center;
  align-items: center;

  border: 1.5px solid var(--t-color-neutral-white);
  border-radius: 50%;
  color: var(--t-color-neutral-white);

  // Hidden in the default (collapsed) state, like the Figma design.
  opacity: 0;
  transform: scale(0.6);
  transition:
    opacity 150ms ease,
    transform 150ms ease;
  pointer-events: none;

  &.green {
    background-color: var(--t-color-green-500);
  }

  &.red {
    background-color: var(--t-color-red-400);
  }

  &.grey {
    background-color: var(--t-color-neutral-400);
  }

  .status-icon {
    --t-icon-size: 0.5rem !important;
  }
}
</style>
